import { Close } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import routes from "../../routes/navRoutes";
import Logo from "../Logo/Logo";
import { UserCircle, GearSix, CaretDown, CaretRight, HandTap } from "@phosphor-icons/react";
import { Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidenavDrawer from "./SidenavDrawer";
import SidenavItem from "./SidenavItem";
import { useAuth } from "../../providers/AuthProvider";
import { isAdmin } from "../../utils/helpers";
import enFlag from "../../assets/flags/en-flag.svg";
import frFlag from "../../assets/flags/fr-flag.svg";
import esFlag from "../../assets/flags/es-flag.svg";
import LaunchCampaignReminder from "../Reminders/LaunchCampaignReminder";
import { useToDo } from "../../providers/ToDoProvider";
import ContactDrawer from "../Dashboard/ContactDrawer";
import Offer from "../Offer/Offer";
import { useSidenav } from "../../providers/SidenavProvider";
import { usePlace } from "../../providers/PlaceProvider";
import { CommonPlaceChip } from "../Inputs/Chips/CommonPlaceChip";
import { HTTPClient } from "../../HTTPClient";
import ComingSoonModal from "../Modals/ComingSoonModal";
import WhatsappOrdersContent from "../Modals/WhatsappOrdersContent";
import WhatsappCampaignContent from "../Modals/WhatsappCampaignContent";
import MissedCallsContent from "../Modals/MissedCallsContent";
import EmailCampaignContent from "../Modals/EmailCampaignContent";
import { WhatsappLogo, Lightning, CurrencyCircleDollar, PhoneCall, ChatCenteredText, Envelope } from "@phosphor-icons/react";
import type { ComingSoonModalId } from "../../routes/navRoutes";

export default function Sidenav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { open, toggleDrawer } = useSidenav();
  const { selectedPlace } = usePlace();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [placeAnchorEl, setPlaceAnchorEl] = useState<null | HTMLElement>(null);
  const [placeLogo, setPlaceLogo] = useState<string>("");
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState<number | null>(null);
  const [openComingSoonModal, setOpenComingSoonModal] = useState<ComingSoonModalId | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const { i18n } = useTranslation();
  const { pendingCount } = useToDo();
  const currentLanguage = (i18n.language || "en").split("-")[0];
  const languageOptions = [
    { code: "fr" as const, label: "Français", flag: frFlag },
    { code: "en" as const, label: "English", flag: enFlag },
    { code: "es" as const, label: "Español", flag: esFlag },
  ];
  const activeLanguage = languageOptions.find((lang) => lang.code === currentLanguage) ?? languageOptions[1];

  const handleLanguageChange = (langCode: "fr" | "en" | "es") => {
    i18n.changeLanguage(langCode);
    if (typeof window !== "undefined") {
      localStorage.setItem("tgc-language", langCode);
    }
    setLangOpen(false);
  };

  useEffect(() => {
    if (!selectedPlace?.id) return;
    HTTPClient.get("/campaign/" + selectedPlace.id)
      .then(({ data }) => {
        const logoUri = data?.configuration?.logo_uri;
        if (logoUri && data?.id) {
          setPlaceLogo(`https://play.thegiftsclub.io/cdn/${data.id}/${logoUri}`);
        } else {
          setPlaceLogo("");
        }
      })
      .catch(() => setPlaceLogo(""));
  }, [selectedPlace?.id]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {/* ComingSoon Modals */}
      <ComingSoonModal
        open={openComingSoonModal === "whatsappOrders"}
        onClose={() => setOpenComingSoonModal(null)}
        bannerTitle={t("nav.orders.title", "Commandes WhatsApp")}
        bannerTag1={t("comingSoon.tag.realtime", "Commandes en temps réel")}
        bannerTag2={t("comingSoon.tag.noapp", "Sans application")}
        subtitle={t("comingSoon.whatsappOrder.subtitle", "Recevez des commandes directement via WhatsApp")}
        features={[
          { icon: WhatsappLogo, text: t("comingSoon.whatsappOrder.feature1", "Commandes directes sur WhatsApp") },
          { icon: Lightning, text: t("comingSoon.whatsappOrder.feature2", "Commandes gérées automatiquement") },
          { icon: CurrencyCircleDollar, text: t("comingSoon.whatsappOrder.feature3", "Plus de ventes, sans application en plus") },
        ]}
      >
        <WhatsappOrdersContent />
      </ComingSoonModal>

      <ComingSoonModal
        open={openComingSoonModal === "whatsappCampaign"}
        onClose={() => setOpenComingSoonModal(null)}
        bannerTitle={t("nav.communications.whatsapp", "Campagnes WhatsApp")}
        bannerTag1={t("comingSoon.tag.openrate", "Taux d'ouverture 95%")}
        bannerTag2={t("comingSoon.tag.formats", "Texte, image, vidéo, lien")}
        subtitle={t("comingSoon.whatsappCampaign.subtitle", "Envoyez des campagnes WhatsApp à vos clients")}
        features={[
          { icon: WhatsappLogo, text: t("comingSoon.whatsappCampaign.feature1", "Envoyer directement via WhatsApp") },
          { icon: Lightning, text: t("comingSoon.whatsappCampaign.feature2", "Automatiser vos relances") },
          { icon: CurrencyCircleDollar, text: t("comingSoon.whatsappCampaign.feature3", "Générer plus de visites et de ventes") },
        ]}
      >
        <WhatsappCampaignContent />
      </ComingSoonModal>

      <ComingSoonModal
        open={openComingSoonModal === "emailCampaign"}
        onClose={() => setOpenComingSoonModal(null)}
        bannerTitle={t("nav.communications.email", "Campagnes Email")}
        bannerTag1={t("comingSoon.emailCampaign.tag1", "Touchez vos clients")}
        bannerTag2={t("comingSoon.emailCampaign.tag2", "Les relancer")}
        bannerGradient="linear-gradient(180deg, #0B33FF 0%, #3A5BFF 50%, #7289FF 100%)"
        bannerGlowColor="#7289FF"
        subtitle={t("comingSoon.emailCampaign.subtitle", "Envoyez des campagnes email à vos clients")}
        features={[
          { icon: Envelope, text: t("comingSoon.emailCampaign.feature1", "Envoyer des emails en quelques clics") },
          { icon: Lightning, text: t("comingSoon.emailCampaign.feature2", "Relancer automatiquement vos clients") },
          { icon: CurrencyCircleDollar, text: t("comingSoon.emailCampaign.feature3", "Générer plus de visites et de ventes") },
        ]}
      >
        <EmailCampaignContent />
      </ComingSoonModal>

      <ComingSoonModal
        open={openComingSoonModal === "missedCalls"}
        onClose={() => setOpenComingSoonModal(null)}
        bannerTitle={t("comingSoon.missedCalls.bannerTitle", "Ne perdez plus aucun appel")}
        bannerTag1={t("comingSoon.missedCalls.tag1", "Relance automatique")}
        bannerTag2={t("comingSoon.missedCalls.tag2", "Clients récupérés")}
        bannerIconSrc="/assets/missed_calls.png"
        bannerGradient="linear-gradient(180deg, #D07B01 0%, #F59000 50%, #FFB142 100%)"
        bannerGlowColor="#F59000"
        subtitle={t("comingSoon.missedCalls.subtitle", "Ne ratez plus aucun appel entrant")}
        features={[
          { icon: PhoneCall, text: t("comingSoon.missedCalls.feature1", "Relance automatique des appels manqués") },
          { icon: ChatCenteredText, text: t("comingSoon.missedCalls.feature2", "SMS personnalisé instantané") },
          { icon: CurrencyCircleDollar, text: t("comingSoon.missedCalls.feature3", "Ventes récupérées automatiquement") },
        ]}
      >
        <MissedCallsContent />
      </ComingSoonModal>

      <SidenavDrawer open={open}>
        <Stack alignItems={"space-between"} height={"100%"} p={2}>
          {/* SIDENAV HEADER */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ bgcolor: "#FAFAFA" }}
          >
            <Logo />
            <IconButton
              aria-label="Close menu"
              onClick={toggleDrawer}
              sx={{
                display: { xs: "flex", lg: "none" },
                bgcolor: "primary.light",
                borderRadius: "8px",
                p: 1,
                "&:hover": {
                  bgcolor: "primary.light",
                },
              }}
            >
              <Close sx={{ color: "#000" }} />
            </IconButton>
          </Stack>

          <Box sx={{ height: "21px" }} />

          {/* SIDENAV BODY */}
          <Stack sx={{ boxSizing: "border-box" }} gap="3px">
            <Stack
              component={Link}
              to="/todo"
              onClick={toggleDrawer}
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{
                textDecoration: "none",
                color: pathname === "/todo" ? "primary.main" : "caption",
                bgcolor: pathname === "/todo" ? "primary.light" : "transparent",
                borderRadius: "4px",
                p: "10px",
                height: "50px",
                cursor: "pointer",
                "&:hover": { bgcolor: "primary.light", color: "primary.main" },
              }}
            >
              <HandTap size={20} weight={pathname === "/todo" ? "fill" : "regular"} style={{ flexShrink: 0 }} />
              <Typography fontWeight={500} fontSize={16} sx={{ flex: 1, whiteSpace: "nowrap" }}>
                {t("nav.todo")}
              </Typography>
              {pendingCount > 0 && (
                <Box
                  sx={{
                    bgcolor: "#F14650",
                    borderRadius: "4px",
                    minWidth: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: "6px",
                    flexShrink: 0,
                  }}
                >
                  <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#fff", fontFamily: "Roboto, sans-serif", lineHeight: 1 }}>
                    {pendingCount}
                  </Typography>
                </Box>
              )}
            </Stack>
            <Divider sx={{ mt: "3px", mb: "8px" }} />
            {routes.map((route, index) => {
              if (!isAdmin(user) && route?.hide?.(user)) return null;
              const active =
                pathname === route.path || (pathname.startsWith(route.path) && route.path !== "/");
              return (
                <SidenavItem
                  key={index}
                  route={route}
                  active={active}
                  handleToggle={toggleDrawer}
                  isSubMenuOpen={openSubMenuIndex === index}
                  onSubMenuToggle={() => setOpenSubMenuIndex(openSubMenuIndex === index ? null : index)}
                  onComingSoon={(modal) => setOpenComingSoonModal(modal as ComingSoonModalId)}
                />
              );
            })}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <LaunchCampaignReminder />
          <Offer />

          <Stack gap="3px" sx={{ bgcolor: { xs: "#FFFFFF", sm: "transparent" }, mx: -2, mb: -2, p: 2 }}>
            {/* Compte */}
            <Stack
              component={Link}
              to="/account"
              onClick={toggleDrawer}
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{
                textDecoration: "none",
                color: pathname === "/account" ? "primary.main" : "#171717",
                bgcolor: pathname === "/account" ? "primary.light" : "transparent",
                borderRadius: "4px",
                p: "10px",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "primary.light",
                  color: "primary.main",
                },
              }}
            >
              <UserCircle size={20} weight={pathname === "/account" ? "fill" : "regular"} />
              <Typography fontWeight={500} fontSize={16}>
                {t("nav.account", "Compte")}
              </Typography>
            </Stack>

            {/* Paramètres - Menu déroulant */}
            <Box sx={{ position: "relative" }}>
              {/* Dropdown menu */}
              <Stack
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  right: 0,
                  bgcolor: "#F0F3FF",
                  borderRadius: "10px",
                  p: 2,
                  mb: 1,
                  opacity: settingsOpen ? 1 : 0,
                  pointerEvents: settingsOpen ? "auto" : "none",
                  transition: "opacity 0.2s ease",
                  zIndex: 10,
                }}
              >
                <Stack
                  onClick={() => { setContactOpen(true); setSettingsOpen(false); }}
                  sx={{
                    color: "#999",
                    borderRadius: "4px",
                    p: "11px 14px",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  <Typography fontWeight={500} fontSize={16}>
                    {t("nav.helpCenter", "Centre d'aide")}
                  </Typography>
                </Stack>
                <Stack
                  onClick={handleLogout}
                  sx={{
                    color: "#999",
                    borderRadius: "4px",
                    p: "11px 14px",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  <Typography fontWeight={500} fontSize={16}>
                    {t("nav.logout", "Déconnexion")}
                  </Typography>
                </Stack>
              </Stack>

              <Stack
              onClick={() => setSettingsOpen(!settingsOpen)}
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{
                color: settingsOpen ? "primary.main" : "#171717",
                borderRadius: "4px",
                p: "10px",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: "primary.light",
                  color: "primary.main",
                },
              }}
            >
              <GearSix size={20} weight={settingsOpen ? "fill" : "regular"} color={settingsOpen ? "#3F5EFB" : "#171717"} />
              <Typography fontWeight={500} fontSize={16} sx={{ flex: 1 }}>
                {t("nav.settings", "Paramètres")}
              </Typography>
              <CaretDown
                size={16}
                style={{
                  transform: settingsOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s ease",
                }}
              />
              </Stack>
            </Box>

            {/* Langue - Menu déroulant */}
            <Box sx={{ position: "relative" }}>
              <Stack
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  right: 0,
                  bgcolor: "#F0F3FF",
                  borderRadius: "10px",
                  p: 2,
                  mb: 1,
                  opacity: langOpen ? 1 : 0,
                  pointerEvents: langOpen ? "auto" : "none",
                  transition: "opacity 0.2s ease",
                  zIndex: 10,
                }}
              >
                {languageOptions.map((lang) => {
                  const isActive = currentLanguage === lang.code;
                  return (
                    <Stack
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code as "fr" | "en" | "es")}
                      sx={{
                        color: isActive ? "primary.main" : "#999",
                        borderRadius: "4px",
                        p: "11px 14px",
                        cursor: "pointer",
                        bgcolor: isActive ? "rgba(63,94,251,0.08)" : "transparent",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.5)",
                        },
                      }}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                    >
                      <Box component="img" src={lang.flag} alt={lang.label} sx={{ width: 20, height: 14, borderRadius: "2px", objectFit: "cover" }} />
                      <Typography fontWeight={500} fontSize={16}>
                        {lang.label}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>

              <Stack
                onClick={() => setLangOpen(!langOpen)}
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  color: langOpen ? "primary.main" : "#171717",
                  borderRadius: "4px",
                  p: "10px",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.main",
                  },
                }}
              >
                <Box component="img" src={activeLanguage.flag} alt={activeLanguage.label} sx={{ width: 20, height: 14, borderRadius: "2px", objectFit: "cover" }} />
                <Typography fontWeight={500} fontSize={16} sx={{ flex: 1 }}>
                  {t("nav.language", "Langue")}
                </Typography>
                <CaretDown
                  size={16}
                  style={{
                    transform: langOpen ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </Stack>
            </Box>

            {/* Enseignes - Desktop only */}
            {!isMobile && (
              <>
                <Stack
                  onClick={(e) => setPlaceAnchorEl(e.currentTarget)}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    color: "#171717",
                    borderRadius: "4px",
                    p: "10px",
                    mt: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "primary.light", color: "primary.main" },
                  }}
                >
                  {placeLogo ? (
                    <Box
                      component="img"
                      src={placeLogo}
                      alt={selectedPlace?.label}
                      sx={{ width: 32, height: 32, borderRadius: "6px", objectFit: "contain" }}
                    />
                  ) : (
                    <Box sx={{ width: 32, height: 32, borderRadius: "6px", bgcolor: "#eee", flexShrink: 0 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={500} fontSize={14} noWrap>
                      {t("nav.allStores", "Toutes les enseignes")}
                    </Typography>
                    <Typography fontSize={12} sx={{ color: "#999" }} noWrap>
                      {t("nav.changeStore", "Changer d'enseigne")}
                    </Typography>
                  </Box>
                  <CaretRight size={16} color="#999" />
                </Stack>
                <CommonPlaceChip open={Boolean(placeAnchorEl)} anchorEl={placeAnchorEl} setAnchorEl={setPlaceAnchorEl} />
              </>
            )}
          </Stack>
        </Stack>
      </SidenavDrawer>
      <ContactDrawer open={contactOpen} onClose={() => setContactOpen(false)} />
    </>
  );
}
